import React, { useState } from "react";
import {
  IconEye,
  IconTrash,
  IconEdit,
  IconUserPlus,
  IconDotsVertical,
  IconPrinter,
  IconCheck,
} from "@tabler/icons-react";
import formatDate from "../../../utilities/helper";
import { useNavigate } from "react-router-dom";
import RoleToggle from "../../../components/RoleToggle";
import NoDataFound from "../../../components/common/NoDataFound";
import Barcode from "../../../components/common/Barcode";
import { useLanguage } from "../../../context/LanguageContext";

export type Picklist = {
  id: string;
  sn: number;
  picklistNumber: string;
  type: string;
  status: string;
  totalOrders: number;
  completedOrders: number;
  warehouseName: string;
  createdByName: string;
  assignedToName: string;
  pickerName?: string;
  createdAt: string;
  startedAt: string;
  completedAt: string;
  barcode: string;
  notes: string;
  assignedTo?: string;
};

type Props = {
  data: {
    picklists: Picklist[];
  };
  columnsData: {
    id?: number;
    text?: string;
    value?: string;
    isShow?: boolean;
  }[];
  currentPage: number;
  limitPage: number;
  isCheckboxEnabled: boolean;
  selectedPicklists: Picklist[];
  onSelectPicklist: (picklist: Picklist) => void;
  onDeletePicklist: (picklistId: string) => void;
  onAssignPicklist?: (picklist: Picklist) => void;
  onPrintPicklist?: (picklist: Picklist) => void;
  onViewDetails?: (picklist: Picklist) => void;
  currentUser?: any;
  onUpdateToPicked?: (picklist: Picklist) => void;
};

const getStatusClass = (status: string) => {
  const normalized = status?.toLowerCase();

  switch (normalized) {
    case "generated":
      return "bg-blue-100 text-blue-800 border border-blue-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800 border border-yellow-800";
    case "in_progress":
      return "bg-indigo-100 text-indigo-800 border border-indigo-800";
    case "completed":
      return "bg-green-100 text-green-800 border border-green-800";
    case "picked":
      return "bg-teal-100 text-teal-800 border border-teal-800";
    case "cancelled":
      return "bg-red-100 text-red-800 border border-red-800";
    case "assigned":
      return "bg-purple-100 text-purple-800 border border-purple-800";
      return "bg-lime-100 text-lime-800 border border-lime-800";
    default:
      return "bg-gray-100 text-gray-800 border border-[#d2d2d2]";
  }
};

const formatStatus = (status: string) => {
  return status
    ?.toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatType = (type: string) => {
  return type
    ?.toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const AllPicklistTable: React.FC<Props> = ({
  data,
  columnsData,
  currentPage,
  limitPage,
  isCheckboxEnabled,
  selectedPicklists,
  onSelectPicklist,
  onDeletePicklist,
  onAssignPicklist,
  onPrintPicklist,
  onViewDetails,
  currentUser,
  onUpdateToPicked,
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const renderCellContent = (
    picklist: Picklist,
    columnValue: string,
    index: number,
  ) => {
    switch (columnValue) {
      case "sn":
        return (
          <span className="text-sm font-medium text-[#212121]">
            {(currentPage - 1) * limitPage + index + 1}
          </span>
        );

      case "picklistNumber":
        return (
          <div className="flex flex-col items-start gap-1 py-1">
            {picklist.picklistNumber ? (
              <div className="p-1 bg-gray-100 rounded w-fit">
                <Barcode
                  value={picklist.picklistNumber}
                  height={28}
                  width={1.2}
                  className="h-8 w-[200px]"
                />
              </div>
            ) : (
              <div className="text-gray-400 text-xs">{t("No Barcode")}</div>
            )}
            <span className="font-bold text-[#212121]">
              {picklist.picklistNumber}
            </span>
          </div>
        );

      case "status":
        const getStatusStyles = (status: string) => {
          const s = status.toLowerCase();
          if (s === "assigned" || s === "picked") return "bg-[#20BF55]";
          if (s === "generated" || s === "pending" || s === "in_progress")
            return "bg-[#FCB910]";
          if (s === "completed") return "bg-[#007BFF]";
          return "bg-gray-500";
        };
        return (
          <span
            className={`px-4 py-1.5 rounded-full text-sm font-bold text-white shadow-sm ${getStatusStyles(
              picklist.status,
            )}`}
          >
            {t(formatStatus(picklist.status))}
          </span>
        );

      case "totalOrders":
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-bold text-[#212121]">
              {t("Total")}: {picklist.totalOrders}
            </span>
            <span className="text-xs text-gray-500 font-medium">
              {t("Pending")}: {picklist.totalOrders - picklist.completedOrders}
            </span>
          </div>
        );
      case "completedOrders": {
        const completed = picklist.completedOrders;
        const total = picklist.totalOrders;
        const percentage = total > 0 ? (completed / total) * 100 : 0;

        return (
          <div className="flex flex-col gap-2 min-w-[120px]">
            <span className="text-sm font-bold text-[#1F2937] leading-none">
              {completed}
            </span>

            <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#EAB308] via-[#84CC16] to-[#10B981] transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      }

      case "warehouseName":
        return (
          <span className="text-sm font-medium text-[#212121]">
            {picklist.warehouseName || ""}
          </span>
        );

      case "createdByName":
        return (
          <span className="text-sm font-medium text-[#212121]">
            {picklist.createdByName || ""}
          </span>
        );

      case "assignedToName":
      case "pickerName":
        return (
          <span className="text-sm font-medium text-[#212121]">
            {picklist.assignedToName || picklist.pickerName || ""}
          </span>
        );

      case "createdAt":
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-[#212121]">
              {formatDate(picklist.createdAt)}
            </span>
            <span className="text-xs text-gray-500">
              {new Date(picklist.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        );

      default:
        return (
          <span className="text-sm text-gray-700">
            {String((picklist as any)[columnValue] || "")}
          </span>
        );
    }
  };

  const [activeTab, setActiveTab] = useState("New Orders");
  const tabs = ["New Orders", "Order Created", "Pick List", "Packaging"];

  return (
    <div className="bg-[#F7F6F2] rounded-3xl">
      <div className="overflow-x-auto overflow-y-auto max-h-[75vh] hide-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner">
        {data.picklists.length === 0 ? (
          <NoDataFound message={t("No picklists found")} />
        ) : (
          <table className="w-full text-sm whitespace-nowrap border-collapse">
            <thead className="text-[#131327]/70 border-b border-dashed border-gray-300 sticky top-0 z-20 bg-[#FCFDF8]">
              <tr className="text-left">
                {isCheckboxEnabled && (
                  <th className="px-6 pt-2 font-semibold text-sm tracking-wider border-r border-dashed border-gray-300">
                    <label className="relative flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            data.picklists.forEach((picklist) => {
                              if (
                                !selectedPicklists.some(
                                  (p) => p.id === picklist.id,
                                )
                              ) {
                                onSelectPicklist(picklist);
                              }
                            });
                          } else {
                            selectedPicklists.forEach((picklist) => {
                              onSelectPicklist(picklist);
                            });
                          }
                        }}
                        checked={
                          selectedPicklists.length === data.picklists.length &&
                          data.picklists.length > 0
                        }
                        className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] checked:text-white transition-all"
                      />
                      <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                        ✓
                      </span>
                    </label>
                  </th>
                )}
                {columnsData.map((col) => (
                  <th
                    key={col.id}
                    className="px-6 pt-2 font-semibold text-sm tracking-wider border-r border-dashed border-gray-300 last:border-none"
                  >
                    {t(col.text || "")}
                  </th>
                ))}
                <th className="px-6 pt-2 font-semibold text-sm tracking-wider text-center">
                  {t("Actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {data.picklists.map((picklist, idx) => (
                <tr
                  key={picklist.id}
                  className="hover:bg-[#FBD037]/10 group transition-colors duration-150"
                >
                  {isCheckboxEnabled && (
                    <td className="px-6 py-4">
                      <label className="relative flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedPicklists.some(
                            (p) => p.id === picklist.id,
                          )}
                          onChange={() => onSelectPicklist(picklist)}
                          className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] checked:text-white transition-all"
                        />
                        <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                          ✓
                        </span>
                      </label>
                    </td>
                  )}
                  {columnsData.map((column) => (
                    <td key={column.id} className="px-6 py-4">
                      {renderCellContent(picklist, column.value || "", idx)}
                    </td>
                  ))}
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => {
                        if (onViewDetails) {
                          onViewDetails(picklist);
                        } else {
                          navigate(`/picklists/${picklist.id}`);
                        }
                      }}
                      className="p-2 border rounded-full bg-[#F7F6F2] hover:bg-[#e8e7e3] text-gray-600 hover:text-[#212121] transition-colors"
                      title={t("View Details")}
                    >
                      <IconEye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AllPicklistTable;
